import { motion } from 'framer-motion';

/**
 * PHASE 6 — the one approved Sign Out confirmation.
 * Current device / current session only. It exposes the existing sign-out behaviour and
 * changes nothing about session revocation, other signed-in sessions, or account deletion.
 * Copy is the Founder-authorized wording (ruling 2026-09-20), not the mockup placeholder.
 */
const COPY = {
  en: {
    title: 'Sign out on this device?',
    body: 'You can sign back in at any time. Your saved Arvexis data will remain in your account.',
    cancel: 'Cancel',
    confirm: 'Sign out',
  },
  es: {
    title: '¿Cerrar sesión en este dispositivo?',
    body: 'Puedes volver a iniciar sesión en cualquier momento. Tus datos guardados de Arvexis permanecerán en tu cuenta.',
    cancel: 'Cancelar',
    confirm: 'Cerrar sesión',
  },
};

export const SignOutConfirm = ({ lang = 'en', prefix = 'settings', onCancel, onConfirm }) => {
  const c = COPY[lang === 'es' ? 'es' : 'en'];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      className="fixed inset-0 z-[120] flex items-center justify-center px-5"
      style={{ background: 'rgba(5, 7, 12, 0.72)', backdropFilter: 'blur(6px)' }}
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      data-testid={`logout-modal-${prefix}`}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 14 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.97, y: 14 }}
        transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 380,
          padding: '30px 26px 26px',
          borderRadius: 22,
          background: 'linear-gradient(180deg, #0B1020 0%, #070A12 100%)',
          border: '1px solid rgba(244, 239, 231, 0.10)',
          boxShadow: '0 30px 80px rgba(0, 0, 0, 0.55)',
        }}
        data-testid={`logout-card-${prefix}`}
      >
        <h2
          style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: 29,
            lineHeight: 1.18,
            fontWeight: 500,
            color: '#F4EFE7',
            margin: 0,
          }}
          data-testid={`logout-title-${prefix}`}
        >
          {c.title}
        </h2>
        <p
          style={{ fontSize: 14.5, lineHeight: 1.6, color: 'rgba(244, 239, 231, 0.6)', margin: '14px 0 26px' }}
          data-testid={`logout-body-${prefix}`}
        >
          {c.body}
        </p>
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              flex: 1,
              height: 48,
              borderRadius: 999,
              background: 'rgba(255, 255, 255, 0.035)',
              border: '1px solid rgba(244, 239, 231, 0.14)',
              color: '#F4EFE7',
              fontSize: 15,
              fontWeight: 500,
              cursor: 'pointer',
            }}
            data-testid={`logout-cancel-${prefix}`}
          >
            {c.cancel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            style={{
              flex: 1,
              height: 48,
              borderRadius: 999,
              background: 'linear-gradient(135deg, #E8C58F 0%, #D4A843 100%)',
              border: '1px solid rgba(232, 197, 143, 0.5)',
              color: '#0A0A0F',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
            }}
            data-testid={`logout-confirm-${prefix}`}
          >
            {c.confirm}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default SignOutConfirm;
